import { useEffect, useRef, useState } from 'react';
import { accessToken } from '@/auth/supabase';

/**
 * What the server pushes. Ids only — see `plugins/realtime.ts` on why no
 * customer data travels over the socket.
 */
export interface CallReceived {
  kind: 'call_received';
  orgId: string;
  callId: number;
  contactId: string;
  leadId: string | null;
  direction: 'inbound' | 'outbound';
}

/** The same call, concluded. Values rather than ids; see the server's note. */
export interface CallSettled {
  kind: 'call_settled';
  orgId: string;
  callId: number;
  outcome: string;
  answered: boolean;
  durationSeconds: number;
}

/**
 * A notification row was just written for one person.
 *
 * Carries no title: the bell is refetched anyway, and reading the newest
 * unread from there keeps one source of truth for what a notification says.
 */
export interface NotificationEvent {
  kind: 'notification';
  orgId: string;
  recipientId: string;
  notificationType: string;
  /**
   * The row's own id, and the only thing that identifies this event.
   *
   * Arrival time cannot: two sockets delivering one notification and one
   * socket delivering two notifications look identical by clock.
   */
  notificationId: number;
}

export type LiveEvent = CallReceived | CallSettled | Omit<NotificationEvent, 'at'>;

/**
 * One call, as the banner needs it: who it is from, and how it ended once it
 * has.
 *
 * MERGED HERE rather than in the banner. The two events carry different
 * fields — the first the ids, the second the conclusion — so a hook that
 * returned only the latest would blank the caller's details the instant the
 * call ended, which is precisely when somebody is reading them.
 */
export interface LiveCall {
  callId: number;
  contactId: string;
  leadId: string | null;
  direction: 'inbound' | 'outbound';
  /** Null while it is still ringing. */
  settled: { outcome: string; answered: boolean; durationSeconds: number } | null;
}

/** Matches the server's `AUTH_PROTOCOL`. */
const AUTH_PROTOCOL = 'crm-auth';
/** The server's close code for a refused token. Reconnecting would just loop. */
const CLOSE_UNAUTHORISED = 4401;

const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

/** http(s) → ws(s), same host. The socket lives on the API, not the site. */
function socketUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';
  return `${base.replace(/^http/, 'ws')}/api/events`;
}

/**
 * The live channel, as a hook.
 *
 * Returns the most recent call event and nothing else: the banner decides
 * what to do with it, and this file stays responsible only for keeping a
 * socket alive.
 *
 * WHY A SOCKET AT ALL. Everything else here polls — the bell on a minute,
 * the Today queue on focus — which is right for work that is waiting and
 * useless for a ringing phone, where a minute is the whole call.
 *
 * IT RECONNECTS, because a socket that died silently is worse than none: the
 * banner simply stops appearing and nobody knows why. A laptop waking from
 * sleep, a proxy dropping an idle connection, a backend deploy — all of them
 * close it, and all of them should be invisible.
 */
/**
 * Everything the socket carries.
 *
 * ONE object rather than one hook per event kind, because there is ONE
 * socket per tab: a second `useLiveEvents` in a second component would open
 * a second connection. The shell calls this once and hands the pieces down.
 */
export interface Live {
  call: LiveCall | null;
  notification: NotificationEvent | null;
}

export function useLiveEvents(enabled: boolean): Live {
  const [latest, setLatest] = useState<LiveCall | null>(null);
  const [notification, setNotification] = useState<NotificationEvent | null>(null);
  /** Survives re-renders so a reconnect does not restart the backoff. */
  const attempt = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let timer: number | undefined;
    /*
     * Guards the whole teardown. React runs effects twice in StrictMode and a
     * route change can unmount mid-connect, so without this a discarded
     * socket keeps reconnecting forever beside the live one.
     */
    let stopped = false;

    const open = async () => {
      if (stopped) return;
      const token = await accessToken();
      // No token means signed out, or a refresh in flight. Either way there
      // is nothing to authenticate with; try again shortly.
      if (!token) {
        timer = window.setTimeout(() => void open(), RECONNECT_MIN_MS);
        return;
      }

      /*
       * The token rides as a SUBPROTOCOL, not in the query string.
       *
       * A browser cannot set an Authorization header on a WebSocket. The
       * alternative — `?token=…` — is written to every access log and proxy
       * log it passes, and an access token is a bearer credential: with this
       * deployment's five-day expiry, anything that can read the log can be
       * the operator for most of a week.
       */
      socket = new WebSocket(socketUrl(), [AUTH_PROTOCOL, token]);

      socket.onopen = () => {
        attempt.current = 0;
      };

      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(String(message.data)) as LiveEvent;

          if (event.kind === 'call_received') {
            setLatest({
              callId: event.callId,
              contactId: event.contactId,
              leadId: event.leadId,
              // Older servers did not send it; inbound is the safe reading,
              // since that is the card this started as.
              direction: event.direction ?? 'inbound',
              settled: null,
            });
            return;
          }

          if (event.kind === 'notification') {
            setNotification(event);
            return;
          }

          if (event.kind === 'call_settled') {
            /*
             * Applied only to the call already on screen. A settle for some
             * other call — one taken on another handset, or one this tab
             * never saw ring — must not conjure a banner for a call that is
             * already over.
             */
            setLatest((current) =>
              current && current.callId === event.callId
                ? {
                    ...current,
                    settled: {
                      outcome: event.outcome,
                      answered: event.answered,
                      durationSeconds: event.durationSeconds,
                    },
                  }
                : current,
            );
          }
        } catch {
          // A frame we cannot read is not worth taking the socket down for.
        }
      };

      socket.onclose = (e) => {
        if (stopped) return;
        /*
         * A refused token is the one close worth respecting. Reconnecting
         * would hammer the server with a credential it has already rejected,
         * and the app's own session handling is what should react to that.
         */
        if (e.code === CLOSE_UNAUTHORISED) return;

        const wait = Math.min(RECONNECT_MIN_MS * 2 ** attempt.current, RECONNECT_MAX_MS);
        attempt.current += 1;
        timer = window.setTimeout(() => void open(), wait);
      };
    };

    void open();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      // 1000 is a normal closure: this end is going away on purpose, which is
      // not something the server should log as a fault.
      socket?.close(1000);
    };
  }, [enabled]);

  return { call: latest, notification };
}
